import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared hook — fetches the dynamic "limited seats" count (auto-decreasing, resets daily)
export const useSeats = () => {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/seats`);
        if (mounted && data && typeof data.seatsLeft === 'number') setSeats(data);
      } catch (_e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000); // refresh every minute so it keeps ticking down
    return () => { mounted = false; clearInterval(t); };
  }, []);
  return seats;
};

// Compact pill — "Only X seats left today"
export const SeatsBadge = ({ className = '' }) => {
  const seats = useSeats();
  if (!seats) return null;
  const { seatsLeft, totalSeats, label } = seats;
  const filled = Math.max(0, Math.min(100, Math.round(((totalSeats - seatsLeft) / Math.max(1, totalSeats)) * 100)));
  return (
    <div className={`inline-flex flex-col items-center gap-1.5 ${className}`}>
      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs sm:text-sm font-bold shadow-sm animate-pulse">
        <Flame className="w-4 h-4 text-orange-500" />
        <span>Only <span className="text-red-700">{seatsLeft}</span> {label || 'seats left today'}</span>
      </div>
      <div className="w-44 sm:w-56 h-1.5 rounded-full bg-red-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-700" style={{ width: `${filled}%` }} />
      </div>
    </div>
  );
};

// Full-width sticky-ish strip — bold urgency banner
export const SeatsBar = ({ onClick }) => {
  const seats = useSeats();
  if (!seats) return null;
  const { seatsLeft, totalSeats } = seats;
  const filled = Math.max(0, Math.min(100, Math.round(((totalSeats - seatsLeft) / Math.max(1, totalSeats)) * 100)));
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white py-2.5 px-4 flex items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm font-semibold hover:brightness-110 transition-all"
    >
      <span className="flex items-center gap-1.5"><Flame className="w-4 h-4 animate-bounce" /> Limited enrollment today!</span>
      <span className="hidden sm:flex items-center gap-1.5"><Users className="w-4 h-4" /> {filled}% seats filled</span>
      <span className="px-2.5 py-0.5 rounded-full bg-white/25 backdrop-blur font-extrabold">Only {seatsLeft} left</span>
      <span className="hidden sm:inline underline underline-offset-2">Claim your seat →</span>
    </button>
  );
};

export default SeatsBadge;
